import type { Theme } from '@src/app/themes';
import { useAuthentication } from '@src/features/auth';
import {
  formatTaskDate,
  TASK_DUE_FILTER_PRIORITIES,
} from '@src/features/tasks/lib';
import {
  subscribeToTaskRealtimeEvents,
  useTasks,
} from '@src/features/tasks/model';
import { resolveTaskActionRoute } from '@src/features/tasks/route';
import type { Task, TaskDueFilter } from '@src/features/tasks/types';
import { useInfiniteScrollSentinel } from '@src/shared/hooks';
import { Button } from '@src/shared/ui/Button';
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
} from '@src/shared/ui/List';
import { Box, Stack } from '@src/shared/ui/layout';
import Tooltip from '@src/shared/ui/Tooltip';
import Typography from '@src/shared/ui/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TaskTypeIcon from '../TaskTypeIcon';

const PAGE_SIZE = 25;

// The panel's content clears the filter when there is one above it. Scoped to
// that case: a section-scoped panel renders no filter, and there the same gap
// would just push the content away from the title for no reason.
const contentOffset = (hasFilter: boolean) => (hasFilter ? '15px' : '5px');

const StyleSheet = {
  list: (hasFilter: boolean) => ({
    marginTop: contentOffset(hasFilter),
    mx: -2.5,
  }),
  listItem: (theme: Theme) => ({
    padding: '6px 20px',
    borderBottom: '1px solid',
    borderColor: 'grey.200',
    gap: '6px',
    '& .MuiListItemText-root': {
      margin: 0,
    },
    '& .MuiListItemText-secondary': {
      color: theme.palette.grey[800],
    },
  }),
  taskIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  empty: (hasFilter: boolean) => ({
    color: 'text.secondary',
    marginTop: contentOffset(hasFilter),
  }),
  error: (hasFilter: boolean) => ({
    alignItems: 'flex-start',
    marginTop: contentOffset(hasFilter),
  }),
  scrollSentinel: {
    height: '1px',
  },
} as const;

type TasksListProps = {
  referenceType?: string;
  /**
   * Which half of the open list to show. Omitted, the list is unnarrowed --
   * a section-scoped panel renders no filter and asks for everything it owns.
   */
  dueFilter?: TaskDueFilter;
};

export default function TasksList({
  dueFilter,
  referenceType,
}: TasksListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthentication();

  // The list the task page opens on, narrowed to one section. Kept in step
  // with `initialFilterState` there: a panel answering a different question
  // than the page it links to reads as one of the two being out of date.
  const filterParams = useMemo(() => {
    // Both "All" and a section-scoped panel ask for the whole open list, so
    // neither sends `priorities` at all.
    const priorities = dueFilter ? TASK_DUE_FILTER_PRIORITIES[dueFilter] : null;

    return {
      task_status: 'pending',
      ...(user?.id ? { task_user: String(user.id) } : {}),
      ...(referenceType ? { task_reference_type: referenceType } : {}),
      ...(priorities ? { priorities } : {}),
    };
  }, [dueFilter, referenceType, user?.id]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isPending,
    isPlaceholderData,
    refetch,
  } = useTasks({ filterParams, pageSize: PAGE_SIZE });

  // Completing a task anywhere writes it into every cached task list, this one
  // included, preserving position rather than dropping rows that stopped
  // matching -- the task page needs the row it just ticked to stay put. Here
  // nothing is being edited, so a closed task is simply no longer an answer.
  const tasks = useMemo(() => {
    // `useTasks` also holds the previous filter's pages on screen while the
    // next request is in flight. On the task page that reads as the list
    // catching up, because the filters sit off to the side. Here the toggle is
    // directly above the list and names what the rows are, so the overdue half
    // shown under "Upcoming" asserts something false. Until the answer lands
    // the panel has nothing to say -- the same blank it shows on first load.
    if (isPlaceholderData) {
      return [];
    }

    return (data?.pages.flatMap((page) => page.results) ?? []).filter(
      (task) => task.status === 'pending',
    );
  }, [data, isPlaceholderData]);

  // A section-scoped panel renders no filter, so nothing sits between the
  // title and the content below.
  const hasFilter = dueFilter !== undefined;

  const scrollSentinelRef = useInfiniteScrollSentinel({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  // These tasks are raised by other people's actions, so without this the
  // panel would sit stale until something on this page caused a fetch.
  useEffect(() => {
    const unsubscribe = subscribeToTaskRealtimeEvents(() => void refetch());
    return () => {
      unsubscribe();
    };
  }, [refetch]);

  const handleOpenTask = useCallback(
    (task: Task) => {
      void navigate(resolveTaskActionRoute(task));
    },
    [navigate],
  );

  // Checked before the empty case: a request that gave up after its retries
  // also leaves nothing to show, and "no open tasks" would read as a cleared
  // queue rather than an answer that never arrived.
  if (isError && tasks.length === 0) {
    return (
      <Stack spacing={1} sx={StyleSheet.error(hasFilter)}>
        <Typography variant="body2" color="text.secondary">
          {t('tasks.load_failed', 'Tasks could not be loaded.')}
        </Typography>
        <Button size="small" variant="outlined" onClick={() => void refetch()}>
          {t('common.try_again', 'Try again')}
        </Button>
      </Stack>
    );
  }

  // Naming the filter back: "No open tasks" under a narrowed list reads as a
  // cleared queue rather than as the half the viewer happens to be looking at.
  if (!isPending && !isPlaceholderData && tasks.length === 0) {
    let emptyMessage = t('tasks.no_open_tasks', 'No open tasks');
    if (dueFilter === 'overdue') {
      emptyMessage = t('tasks.no_overdue_tasks', 'No overdue tasks');
    } else if (dueFilter === 'upcoming') {
      emptyMessage = t('tasks.no_upcoming_tasks', 'No upcoming tasks');
    }
    // "All" keeps the plain wording: nothing is narrowed, so the queue really
    // is empty.

    return (
      <Typography variant="body2" sx={StyleSheet.empty(hasFilter)}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <>
      <List disablePadding sx={StyleSheet.list(hasFilter)}>
        {tasks.map((task) => {
          const content = (
            <>
              <ListItemAvatar>
                <Box sx={StyleSheet.taskIconWrapper}>
                  <TaskTypeIcon task={task} />
                </Box>
              </ListItemAvatar>
              <ListItemText
                primary={task.taskType.name}
                secondary={formatTaskDate(task.dueDate)}
                slotProps={{
                  primary: { variant: 'body2' },
                  secondary: { variant: 'body3' },
                }}
              />
            </>
          );

          // The row names the type and when it is due; what the task actually
          // asks for is the description, which is a sentence and too long to
          // sit in a panel this narrow. An empty title renders no tooltip at
          // all, which is what should happen for a task that has no
          // description rather than an empty box on hover.
          return (
            <Tooltip key={task.id} title={task.description || ''}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleOpenTask(task)}
                  sx={StyleSheet.listItem}
                >
                  {content}
                </ListItemButton>
              </ListItem>
            </Tooltip>
          );
        })}
      </List>
      {hasNextPage && (
        <Box ref={scrollSentinelRef} sx={StyleSheet.scrollSentinel} />
      )}
    </>
  );
}
